import { useCallback, useEffect, useState } from "react";
import { api } from "../../api.js";
import {
  Badge, Button, EmptyState, Eyebrow, Field, LoadingRows, Notice, Panel, SelectField,
  Table, TableCell, TableHead, TableHeader, TableRow, TextAreaField,
} from "../../components/ui.jsx";

const EMPTY_FIELD = {
  entity_type: "company", key: "", label: "", type: "text", description: "",
  default_ttl_days: "90", sensitivity: "public", lifecycle: "active", options: "",
};

function FieldForm({ value, onSaved, onCancel }) {
  const [form, setForm] = useState(value ? {
    entity_type: value.entity_type,
    key: value.key,
    label: value.label,
    type: value.type,
    description: value.description || "",
    default_ttl_days: value.default_ttl_days || "",
    sensitivity: value.sensitivity,
    lifecycle: value.lifecycle,
    options: (value.options || []).join(", "),
  } : EMPTY_FIELD);
  const [state, setState] = useState({ busy: false, error: "" });
  const set = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  async function submit(event) {
    event.preventDefault();
    setState({ busy: true, error: "" });
    const input = {
      label: form.label,
      type: form.type,
      description: form.description,
      default_ttl_days: form.default_ttl_days ? Number(form.default_ttl_days) : null,
      sensitivity: form.sensitivity,
      options: ["select", "multi_select"].includes(form.type)
        ? form.options.split(",").map((item) => item.trim()).filter(Boolean)
        : null,
      ...(value ? { lifecycle: form.lifecycle } : { entity_type: form.entity_type, key: form.key }),
    };
    try {
      const data = value
        ? await api.updatePlatformCatalogField(value.id, input)
        : await api.createPlatformCatalogField(input);
      onSaved(data.field);
    } catch (error) {
      setState({ busy: false, error: error.message });
    }
  }

  return (
    <Panel>
      <form className="grid gap-5 sm:grid-cols-2" onSubmit={submit}>
        <div className="col-span-full flex items-center justify-between">
          <div className="grid gap-1"><Eyebrow>Global schema</Eyebrow><h2 className="text-xl font-bold tracking-[-0.01em]">{value ? `Edit ${value.label}` : "Create a global field"}</h2></div>
          <Button variant="ghost" onClick={onCancel}>Close</Button>
        </div>
        {state.error && <div className="col-span-full"><Notice>{state.error}</Notice></div>}
        {!value && <SelectField label="Entity type" value={form.entity_type} onChange={set("entity_type")}><option value="company">Company</option><option value="person">Person</option></SelectField>}
        <Field label="Field key" hint={value ? "cannot be changed" : "lowercase and underscores"} value={form.key} onChange={set("key")} disabled={Boolean(value)} required />
        <Field label="Display label" value={form.label} onChange={set("label")} required />
        <SelectField label="Value type" value={form.type} onChange={set("type")}><option value="text">Text</option><option value="number">Number</option><option value="boolean">Yes / no</option><option value="date">Date</option><option value="url">URL</option><option value="select">Dropdown</option><option value="multi_select">Multi-select</option><option value="json">Structured JSON</option></SelectField>
        <Field label="Freshness window" hint="days" type="number" min="1" max="3650" value={form.default_ttl_days} onChange={set("default_ttl_days")} />
        <SelectField label="Sensitivity" value={form.sensitivity} onChange={set("sensitivity")}><option value="public">Public</option><option value="professional">Professional</option><option value="sensitive">Sensitive</option></SelectField>
        {value && <SelectField label="Lifecycle" value={form.lifecycle} onChange={set("lifecycle")}><option value="active">Active</option><option value="archived">Archived</option></SelectField>}
        {["select", "multi_select"].includes(form.type) && <Field label="Options" hint="comma-separated" value={form.options} onChange={set("options")} required />}
        <div className="sm:col-span-2"><TextAreaField label="Description" value={form.description} onChange={set("description")} /></div>
        <div className="col-span-full flex justify-end gap-2"><Button onClick={onCancel}>Cancel</Button><Button variant="primary" type="submit" disabled={state.busy}>{state.busy ? "Saving…" : value ? "Save field" : "Create field"}</Button></div>
      </form>
    </Panel>
  );
}

export default function FieldsTab({ onChanged }) {
  const [editing, setEditing] = useState(undefined);
  const [state, setState] = useState({ loading: true, fields: [], error: "" });

  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const data = await api.platformCatalogFields();
      setState({ loading: false, fields: data.fields, error: "" });
    } catch (error) {
      setState((current) => ({ ...current, loading: false, error: error.message }));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function saved() {
    setEditing(undefined);
    await Promise.all([load(), onChanged()]);
  }

  if (editing !== undefined) return <FieldForm value={editing} onCancel={() => setEditing(undefined)} onSaved={saved} />;
  return (
    <div className="grid gap-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-xs leading-5 text-muted-foreground">These definitions apply to every RevPal customer and agent catalog query.</p>
        <Button variant="primary" onClick={() => setEditing(null)}>New global field</Button>
      </div>
      {state.error && <Notice>{state.error}</Notice>}
      {state.loading ? <LoadingRows count={6} /> : state.fields.length ? (
        <Table>
          <TableHead><TableHeader>Field</TableHeader><TableHeader>Entity</TableHeader><TableHeader>Type</TableHeader><TableHeader>TTL</TableHeader><TableHeader>Coverage</TableHeader><TableHeader>Status</TableHeader><TableHeader><span className="sr-only">Edit</span></TableHeader></TableHead>
          <tbody>{state.fields.map((field) => (
            <TableRow key={field.id}>
              <TableCell><strong className="block text-[13px]">{field.label}</strong><small className="font-mono text-[11px] text-muted-foreground">{field.key}</small></TableCell>
              <TableCell className="capitalize">{field.entity_type}</TableCell>
              <TableCell>{field.type.replaceAll("_", " ")}</TableCell>
              <TableCell>{field.default_ttl_days ? `${field.default_ttl_days} days` : "No expiry"}</TableCell>
              <TableCell>{field.company_coverage} companies</TableCell>
              <TableCell><Badge status={field.lifecycle}>{field.lifecycle}</Badge></TableCell>
              <TableCell className="text-right"><Button variant="ghost" className="px-2" onClick={() => setEditing(field)}>Edit</Button></TableCell>
            </TableRow>
          ))}</tbody>
        </Table>
      ) : <EmptyState title="No global fields" description="Create the first platform-level field definition." />}
    </div>
  );
}
